import { StackMeasurements } from "@foresting-tomorrow/forest_tools_inventory";
import { chipsCalculation, displayInventoryValue } from "./utils/calculations";
import { ChangeEvent } from "react";
import { Divider, Grid, Typography } from "@mui/material";

import Selector from "components/select/selector";
import { useTranslation } from "utils/locales/utilityFunctions";
import { beautifyNumber } from "utils/numberModification";
import TextFieldInput from "components/input/textField";
import MeasurementSectionFields from "./measurementSectionFields";
import { FirestoreInventoryItemProps } from "types/auth";
import { RawWoodSpeciesItem } from "./rawWoodInputInformationSpecies";
import useSpeciesData from "hooks/useSpeciesData";

// Data
const sectionNumbers = [...Array(1001).keys()].slice(1); // [1..1000]
const SECTION_OPTIONS = sectionNumbers.map((el) => ({
  id: el.toString(),
  status: "enabled",
  label: el.toString(),
}));

const CF_OPTIONS = Array.from({ length: 19 }, (_, i) => {
  const val = 10 + 5 * i;
  return {
    id: val.toString(),
    label: `${val}%`,
    status: "enabled",
  };
});

interface RawWoodInputQuantitiesProps {
  variant: FirestoreInventoryItemProps["inventoryType"];
  disableInputs: boolean;
  buyerApprovedDisable: boolean;
  isChipped: boolean;
  manualVolume: string;
  stackLength: string;
  averageLogLength: string;
  averageStackHeight: string;
  numericMeasurements: StackMeasurements | null;
  numberOfSections: number | "";
  heights: string[];
  manualCompFactor: string;
  species?: string | RawWoodSpeciesItem[];
  setManualVolume: (event: ChangeEvent<HTMLInputElement>) => void;
  setStackLength: (value: string) => void;
  setNumberOfSections: (value: number | "") => void;
  setHeights: (heights: string[]) => void;
  setManualCompFactor: (value: string) => void;
}

const RawWoodInputQuantities = ({
  variant,
  disableInputs,
  buyerApprovedDisable,
  isChipped,
  manualVolume,
  stackLength,
  averageLogLength,
  averageStackHeight,
  numericMeasurements,
  numberOfSections,
  heights,
  manualCompFactor,
  species,
  setManualVolume,
  setStackLength,
  setNumberOfSections,
  setHeights,
  setManualCompFactor,
}: RawWoodInputQuantitiesProps) => {
  const { translate } = useTranslation();
  const { getSpeciesType } = useSpeciesData();

  // Find species type (foliage, needle, mixed)
  let chipSpeciesTypes: string[] = [];
  let speciesType: "foliage" | "needle" | "mixed" | undefined = undefined;
  if (species && typeof species === "string") {
    const type = getSpeciesType(species);
    if (type === "foliage" || type === "needle") {
      speciesType = type;
    }
  } else if (species && Array.isArray(species)) {
    species.forEach((spc) => {
      const type = getSpeciesType(spc.id);
      if (type && !chipSpeciesTypes.includes(type)) {
        chipSpeciesTypes.push(type);
      }
    });
    if (chipSpeciesTypes.length === 1) {
      speciesType = chipSpeciesTypes[0] as "foliage" | "needle";
    } else if (chipSpeciesTypes.length > 1) {
      speciesType = "mixed";
    }
  }

  // Calculate volume based on stack length, number of sections, and conversion factor
  let result = { stackedVolumeM3: 0, volumeM3: 0, greenMassTon: 0, chippedVolumeM3: 0 };
  // Convert strings to numbers
  const averageLogLengthMNum = Number(averageLogLength.replace(",", "."));
  const manualCompFactorNum = isNaN(parseFloat(manualCompFactor.replace(",", ".")))
    ? 0
    : parseFloat(manualCompFactor.replace(",", "."));

  if (
    speciesType !== undefined &&
    numericMeasurements !== null &&
    averageLogLengthMNum > 0 &&
    manualCompFactorNum > 0
  ) {
    const calculated = chipsCalculation(numericMeasurements ? {
      measurements: numericMeasurements,
      species: speciesType,
      logAverageLengthM: averageLogLengthMNum,
      correctionPercentage: manualCompFactorNum,
    } : null);
    if (calculated) result = {
      stackedVolumeM3: displayInventoryValue(calculated.totals.stackedVolumeM3, "volumeM3"),
      volumeM3: displayInventoryValue(calculated.totals.volumeM3, "volumeM3"),
      greenMassTon: displayInventoryValue(calculated.totals.greenMassTon, "massTon"),
      chippedVolumeM3: displayInventoryValue(calculated.totals.chippedVolumeM3, "volumeM3"),
    };
  }

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5">{translate("raw-wood-input-quantities-enter-volume")}</Typography>
      </Grid>

      <Grid item xs={12} sm={12 / 3}>
        <TextFieldInput
          title={translate("raw-wood-input-quantities-cubic-volumen")}
          value={manualVolume}
          handleChange={setManualVolume}
          fullWidth
          showLabel
          disabled={disableInputs || buyerApprovedDisable}
          inputAdornment={translate("raw-wood-input-rm")}
        />
      </Grid>

      {!isChipped && (
        <>
          <Grid item xs={6} sm={12}>
            <Typography color={"GrayText"}>
              ... {translate("raw-wood-input-quantities-or-calculate")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5">{translate("raw-wood-input-quantities-dimensions")}</Typography>
          </Grid>

          <Grid item xs={6} sm={12 / 3}>
            <Selector
          title={translate("raw-wood-input-quantities-stack-length")}
          value={stackLength.toString()}
          options={Array.from({ length: 1000 }, (_, i) => ({
            id: (i + 1).toString(),
            label: `${i + 1} m`,
            status: "enabled",
          }))}
          label={true}
          fullWidth={true}
          error={false}
          disabled={disableInputs || buyerApprovedDisable}
          changeHandler={(e) => {
            const val = e.target.value;
            setStackLength(val);
          }}
            />
          </Grid>

          <Grid item xs={6} sm={12 / 3}>
            <Selector
          title={translate("raw-wood-input-quantities-number-of-sections")}
          value={numberOfSections.toString()}
          options={SECTION_OPTIONS}
          label={true}
          fullWidth={true}
          error={false}
          disabled={disableInputs || buyerApprovedDisable}
          changeHandler={(e) => {
            const val = e.target.value;
            setNumberOfSections(val === "" ? "" : Number(val));
          }}
            />

            {stackLength !== "" && numberOfSections !== "" && numberOfSections > 0 && (
          <Typography variant="caption" color={disableInputs || buyerApprovedDisable ? "GrayText" : "text.primary"}>
            {translate("raw-wood-input-quantities-length-per-section")}{" "}
            {beautifyNumber(parseInt(stackLength) / numberOfSections, 1)} m
          </Typography>
            )}
          </Grid>

          {typeof numberOfSections === "number" && numberOfSections > 0 && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <MeasurementSectionFields
            disableInputs={disableInputs || buyerApprovedDisable}
            sectionCount={numberOfSections}
            frontTitle={translate("raw-wood-input-quantities-measurements")}
            frontHeights={heights}
            setFrontHeights={setHeights}
          />

          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5">
              {translate("raw-wood-input-quantities-calculations")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={12 / 4}>
            <Selector
              title={translate("raw-wood-input-quantities-conversion-factor")}
              value={manualCompFactor}
              options={CF_OPTIONS}
              label={true}
              fullWidth={true}
              error={false}
              disabled={disableInputs || buyerApprovedDisable}
              changeHandler={(e) => {
                const val = e.target.value;
                setManualCompFactor(val);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12 / 4}>
            <TextFieldInput
              title={translate("input-dialog-raw-wood-input-conversion-factor-cubic-metres")}
              value={beautifyNumber(result.stackedVolumeM3, 2)}
              showLabel={true}
              fullWidth={true}
              disabled={disableInputs || buyerApprovedDisable}
              inputAdornment={translate("raw-wood-input-rm")}
            />
          </Grid>

          <Grid item xs={12} sm={12 / 4}>
            <TextFieldInput
              title={translate("input-dialog-raw-wood-input-conversion-factor-volume")}
              value={beautifyNumber(result.volumeM3, 2)}
              showLabel={true}
              fullWidth={true}
              disabled={disableInputs || buyerApprovedDisable}
              inputAdornment={"m3"}
            />
          </Grid>

          <Grid item xs={12} sm={12 / 4}>
            <TextFieldInput
              title={translate("input-dialog-raw-wood-input-processed-chips")}
              value={beautifyNumber(result.chippedVolumeM3, 2)}
              showLabel={true}
              fullWidth={true}
              disabled={disableInputs || buyerApprovedDisable}
              inputAdornment={translate("raw-wood-input-rm")}
            />
          </Grid>
        </>
          )}
        </>
      )}
    </>
  );
};

export default RawWoodInputQuantities;
